import type { PrintPayloadOptions, PrintReceiptSource } from './types'
import { buildCustomSections } from './derive/customSections'
import {
  deriveIdentifiers,
  deriveReceiptId,
  deriveReference,
  withCopySuffix,
} from './derive/identifiers'
import { deriveDateString } from './derive/labels'

/**
 * Everything both printer interfaces need, in the order the previous builder
 * applied it. The order is load-bearing in two places:
 *
 *  - a preview DELETES `PaymentType` before the multiple-payment check runs, so
 *    a preview never gets a "Payment Detail" footer;
 *  - `DiscountText` is overwritten with the legacy Thai label only AFTER the
 *    summary rows have been derived, because the summary rows use the English
 *    checkout label. The two surfaces have always disagreed.
 */
export const buildBasePayload = (
  source: PrintReceiptSource,
  options: PrintPayloadOptions,
  now?: number,
): Record<string, any> => {
  const isPreview = options.isPreview || false

  let ids = deriveIdentifiers(source, now)
  if (options?.isCopy) ids = withCopySuffix(ids)

  const payload: Record<string, any> = {
    ...source,
    ...ids,
    Reference: deriveReference(source),
    FullAlias: deriveReference(source),
    DateString: deriveDateString(source),
    Cashier: source?.User,
    EnableHeaderItems: true,
    isPreview: false,
  }

  if (isPreview) {
    payload.isPreview = true
    payload.IsPreview = true
    delete payload.PaymentType
  } else {
    const receiptId = deriveReceiptId(source)
    if (receiptId !== undefined) payload.ReceiptId = receiptId
  }

  if (options.enableCustomerCount === false) {
    delete payload.CustomerCount
  } else if (payload.CustomerCount != null) {
    // The legacy print service types CustomerCount as a string and renders it
    // when non-empty && != "0". A raw JSON int risks a deserialization
    // mismatch. "0" is fine — the service skips it.
    payload.CustomerCount = String(payload.CustomerCount)
  }

  // Reads payload.PaymentType (already deleted on a preview), not source.
  const custom = buildCustomSections(payload, options)
  payload.CustomHeaders = custom.CustomHeaders
  payload.CustomFooters = custom.CustomFooters
  if (custom.PaymentType !== undefined) payload.PaymentType = custom.PaymentType

  return payload
}
